import React from 'react';
import { Phone } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';

const QuoteDetailModal = ({ isOpen, quote, onClose }) => {
  if (!quote) return null;

  const formatPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 13) {
      return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
    }
    if (digits.length === 11) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }
    return phone;
  };

  const calculateTotal = () => {
    return quote.items?.reduce((total, item) => {
      if (item.unit_price) {
        return total + (item.unit_price * item.quantity);
      }
      return total;
    }, 0) || 0;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Orçamento #{quote.id?.slice(0, 8).toUpperCase()}</span>
            <span className="text-sm font-normal text-slate-500">
              {new Date(quote.created_at).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Customer Info */}
          <div className="bg-teal-50 rounded-xl p-4">
            <h3 className="font-semibold text-teal-800 mb-3">Dados do Cliente</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-teal-600">Nome</p>
                <p className="font-medium text-slate-800">{quote.customer_name}</p>
              </div>
              <div>
                <p className="text-xs text-teal-600">WhatsApp</p>
                <p className="font-medium text-slate-800">{formatPhone(quote.customer_phone)}</p>
              </div>
              {quote.customer_email && (
                <div className="md:col-span-2">
                  <p className="text-xs text-teal-600">E-mail</p>
                  <p className="font-medium text-slate-800">{quote.customer_email}</p>
                </div>
              )}
            </div>
          </div>

          {/* Items */}
          <div>
            <h3 className="font-semibold text-slate-800 mb-3">Itens do Pedido</h3>
            <div className="space-y-3">
              {quote.items?.map((item, idx) => (
                <div key={idx} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-slate-800">{item.product_name}</p>
                      <p className="text-lg text-teal-600 font-bold">{item.quantity} unidades</p>
                    </div>
                    {item.unit_price && (
                      <div className="text-right">
                        <p className="text-sm text-slate-500">R$ {item.unit_price.toFixed(2)}/un</p>
                        <p className="font-semibold text-slate-800">
                          R$ {(item.unit_price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>
                  {item.customization_notes && (
                    <div className="bg-amber-50 p-3 rounded-lg mt-2">
                      <p className="text-xs text-amber-700 font-medium mb-1">Observações:</p>
                      <p className="text-slate-700">{item.customization_notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Total */}
          {calculateTotal() > 0 && (
            <div className="bg-slate-100 rounded-xl p-4 text-right">
              <p className="text-sm text-slate-500">Total Estimado</p>
              <p className="text-2xl font-bold text-teal-600">
                R$ {calculateTotal().toFixed(2)}
              </p>
            </div>
          )}

          {/* General Notes */}
          {quote.notes && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <h3 className="font-semibold text-amber-800 mb-2">Observações Gerais</h3>
              <p className="text-slate-700">{quote.notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <a
              href={`https://wa.me/${quote.customer_phone?.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors font-medium"
            >
              <Phone className="w-5 h-5" />
              Abrir WhatsApp
            </a>
            <Button variant="outline" onClick={onClose} className="px-6">
              Fechar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteDetailModal;
